import {CrudForm, FormLabel} from "@components";
import {translate} from "@util";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SvgIconOwnProps,
} from "@mui/material";
import {useThemeContext} from "@theme";
import {BaseEntity, BaseEntityForm, entity} from "@core/model";
import {CloseOutlined} from "@mui/icons-material";
import {useDownBreakpoint} from "@hooks";

export type EditDialogProps<T extends BaseEntity> = {
  entity: entity;
  parentId?: string;
  open: boolean;
  formLabel?: Partial<FormLabel>;
  EntityIcon?: React.ComponentType<SvgIconOwnProps>;
  form: BaseEntityForm;
  data: Partial<T>;
  selectValues?: {[key: string]: {id: string | number; name: string}[]};
  isLoading?: boolean;
  isFetching?: boolean;
  onConfirm: (e: Partial<T>) => void;
  onClose?: () => void;
  onFieldChange?: (field: string, value: any) => void;
  onReset?: () => void;
};

function EditDialog<T extends BaseEntity>(props: EditDialogProps<T>) {
  const {
    open,
    entity,
    EntityIcon,
    data,
    form,
    formLabel,
    isFetching,
    isLoading,
    selectValues,
    onReset,
    onFieldChange,
    onClose,
    onConfirm,
  } = props;

  const {theme} = useThemeContext();

  const isDownSm = useDownBreakpoint("sm");
  const action = data?.id ? "edit" : "add";

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      fullScreen={isDownSm}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      {isDownSm && onClose && (
        <IconButton
          onClick={() => onClose()}
          sx={{position: "absolute", top: 16, right: 16}}>
          <CloseOutlined />
        </IconButton>
      )}
      {EntityIcon && (
        <Stack direction="row" mt={2} justifyContent="center">
          <Box
            sx={{
              bgcolor: theme.palette.secondary.dark,
              width: "64px",
              borderRadius: "32px",
              height: "64px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <EntityIcon
              fontSize="large"
              sx={{
                color: theme.palette.getContrastText(
                  theme.palette.secondary.dark,
                ),
              }}
            />
          </Box>
        </Stack>
      )}
      <DialogTitle align="center">
        {action === "add"
          ? `${translate("common.action.new")} ${translate(`entity.${entity}.title`)}`
          : data.name}
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: isDownSm ? 2 : 3,
        }}>
        <CrudForm
          data={data}
          form={form}
          label={formLabel}
          isFetching={isFetching}
          isLoading={isLoading}
          selectValues={selectValues}
          onSave={onConfirm}
          onReset={onReset}
          onFieldChange={onFieldChange}
          sx={{
            flex: 1,
            overflow: "hidden",
            "& .crud-form-fields-container": {
              flex: 1,
              overflow: "auto",
              px: "2px",
            },
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export {EditDialog};
